import { useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, Calendar, User } from 'lucide-react';
import { formatCurrency } from '@/utils/formatting';
import { cn } from '@/lib/utils';

interface Proposal {
  id: number;
  proposalName: string;
  proposalNumber: number;
  customerName: string;
  status: string;
  totalAmount: number;
  createdDate: string;
}

interface CleanProposalListProps {
  proposals: Proposal[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSelectProposal: (proposal: Proposal) => void;
  selectedProposalId?: number | null;
}

export function CleanProposalList({
  proposals,
  loading,
  hasMore,
  onLoadMore,
  onSelectProposal,
  selectedProposalId,
}: CleanProposalListProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'secondary';
      case 'sent':
      case 'submitted':
        return 'default';
      case 'approved':
      case 'accepted':
        return 'success';
      case 'rejected':
      case 'declined':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const ProposalCard = ({ proposal }: { proposal: Proposal }) => {
    const isSelected = selectedProposalId === proposal.id;
    
    return (
      <Card
        className={cn(
          "p-4 cursor-pointer transition-all duration-200",
          "hover:shadow-md hover:border-primary/50",
          isSelected && "ring-2 ring-primary shadow-md bg-primary/5"
        )}
        onClick={() => onSelectProposal(proposal)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm">
                Proposal #{proposal.proposalNumber}
              </span>
              <Badge variant={getStatusColor(proposal.status)} className="text-xs">
                {proposal.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground truncate">
              {proposal.proposalName}
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{proposal.customerName}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{formatDate(proposal.createdDate)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-sm">
                {formatCurrency(proposal.totalAmount)}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </Card>
    );
  };

  const LoadingCard = () => (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-3 w-48 mb-2" />
          <div className="flex items-center gap-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );

  if (loading && proposals.length === 0) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <LoadingCard key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4">
      {proposals.map((proposal) => (
        <ProposalCard key={proposal.id} proposal={proposal} />
      ))}
      
      {hasMore && (
        <div ref={loadMoreRef} className="py-4">
          {loading ? (
            <LoadingCard />
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              Loading more...
            </div>
          )}
        </div>
      )}
    </div>
  );
}